import React, { useRef, useState } from "react";
import axios from "axios";
import { Loader2, Upload } from "lucide-react";
import { toast } from "../../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

const COLS = {
  name: ["nome", "name", "cliente", "customer", "full name", "nome completo", "nome cliente"],
  first: ["first name", "firstname", "nome ", "prenom"],
  last: ["last name", "lastname", "cognome", "surname"],
  phone: ["telefono", "phone", "tel", "cellulare", "mobile", "phone number", "numero"],
  email: ["email", "e-mail", "mail"],
  visits: ["visite", "visits", "prenotazioni", "bookings", "n visite", "reservations"],
  last_visit: ["ultima visita", "last visit", "ultima", "last booking", "ultima prenotazione", "data"],
  notes: ["note", "notes", "commenti", "tags"],
};

function splitLine(line, sep) {
  const out = []; let cur = ""; let inQ = false;
  for (const ch of line) {
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === sep && !inQ) { out.push(cur); cur = ""; continue; }
    cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

export function parseCustomersCsv(text) {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
  if (lines.length < 2) return [];
  const sep = [";", ",", "\t"].sort((a, b) => lines[0].split(b).length - lines[0].split(a).length)[0];
  const header = splitLine(lines[0], sep).map((h) => h.toLowerCase().trim());
  const idx = (key) => header.findIndex((h) => COLS[key].some((k) => h === k.trim() || h.startsWith(k.trim())));
  const iName = idx("name"), iFirst = idx("first"), iLast = idx("last"), iPhone = idx("phone");
  const iEmail = idx("email"), iVisits = idx("visits"), iLast_ = idx("last_visit"), iNotes = idx("notes");
  const toIso = (v) => {
    if (!v) return "";
    const m = v.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);
    if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
    return v.slice(0, 10);
  };
  return lines.slice(1).map((l) => {
    const c = splitLine(l, sep);
    const name = iName >= 0 ? c[iName] : [c[iFirst], c[iLast]].filter(Boolean).join(" ");
    const visits = iVisits >= 0 ? parseInt(c[iVisits], 10) : NaN;
    return {
      name: name || "",
      phone: iPhone >= 0 ? c[iPhone] : "",
      email: iEmail >= 0 ? c[iEmail] : "",
      visits: Number.isNaN(visits) ? null : visits,
      last_visit: iLast_ >= 0 ? toIso(c[iLast_]) : "",
      notes: iNotes >= 0 ? c[iNotes] : "",
    };
  }).filter((r) => r.name && (r.phone || r.email));
}

export function ImportCustomersCsv({ onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const rows = parseCustomersCsv(await file.text());
      if (!rows.length) { toast({ title: "Nessun cliente riconosciuto", description: "Servono almeno le colonne Nome e Telefono (o Email)." }); return; }
      if (!window.confirm(`Importare ${rows.length} clienti come fonte Ristorante? I duplicati (stesso telefono/email) verranno aggiornati, non raddoppiati.`)) return;
      const { data } = await axios.post(`${API}/admin/customers/import`, { customers: rows, source_tag: "ristorante", imported_from: file.name }, { headers: authHeaders() });
      toast({ title: "Import completato", description: `${data.added} nuovi · ${data.updated} aggiornati · ${data.skipped} scartati` });
      onDone?.();
    } catch (err) {
      toast({ title: "Errore import", description: err?.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };

  return (
    <>
      <input ref={ref} type="file" accept=".csv,.txt,text/csv" className="hidden" onChange={onFile} data-testid="customers-import-file" />
      <button
        onClick={() => ref.current?.click()}
        disabled={busy}
        data-testid="customers-import-csv"
        title="Importa clienti da CSV (es. export TheFork): colonne Nome, Telefono, Email, Visite, Ultima visita"
        className="inline-flex items-center gap-1.5 rounded-full border border-[#4a1c17] text-[#4a1c17] hover:bg-[#faf3ee] text-sm px-4 py-1.5 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importa CSV
      </button>
    </>
  );
}
